<template>
  <div class="login text-center">
    <div class="form-signin shadow">
      <div class="form-signin-inner m-auto">

        <EmiotHeaderImage></EmiotHeaderImage>

        <div v-if="!showEmailInput && !errorMsg">Signing in...</div>
        <form class="m-0" v-if="showEmailInput" v-on:submit.prevent="submit()">
          <div>Almost there! We just need you to confirm your email to complete the magic login.</div>
          <br>
          <label for="userName" class="sr-only">Email</label>
          <input type="text" id="userName" class="form-control form-control-lg mb-4" placeholder="Email" required autofocus
                 v-model="email">

          <button type="submit" class="btn btn-lg btn-success btn-block mb-2" v-bind:disabled="btnDisabled">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="btnDisabled"></span>
            Sign in
          </button>
        </form>
        <div class="alert alert-danger" role="alert" v-if="errorMsg">{{errorMsg}}</div>
      </div>
    </div>
    <p class="position-absolute text-muted copyright">&copy; 2018-2019</p>
  </div>
</template>

<style>
    div.login {
        padding-top: 40px;
        padding-bottom: 40px;
    }

    div.login .copyright {
        bottom: 0;
        padding-left: 20px;
    }

    .form-signin {
        background-color: rgba(255,255,255,.95);
        color: rgb(167,167,167);
        padding: 32px;
        margin-left: auto;
        margin-right: auto;
        width: calc(100% - 24px);
    }

    .form-signin-inner {
        max-width: 360px;
        width: 100%;
    }

    .form-signin .header {
        margin-bottom: 24px;
    }

    .form-signin .checkbox {
        font-weight: 400;
    }

    .form-signin .form-control {
        position: relative;
        box-sizing: border-box;
        background-color: rgb(244,244,244);
    }

    .form-signin .form-control:focus {
        z-index: 2;
    }


    @media only screen and (min-width: 768px) {
        div.login {
            height: 100%;
            display: -ms-flexbox;
            display: flex;
            -ms-flex-align: center;
            align-items: center;
        }
        .form-signin {
            padding: 133px 32px;
            max-width: 608px;
            width: 100%;
            margin-left: 106px;
        }
        .form-signin .header {
            margin-bottom: 88px;
        }
    }

</style>

<script>
import EmiotHeaderImage from '../components/register-user/EmiotHeaderImageComponent.vue'
import {firebaseAuth, translateFirebaseAuthError} from "@/firebase";
import {signInWithEmailLink, signInWithCustomToken} from 'firebase/auth';
import {getBrandingOptions} from "@/utils/getBrandingOptions";

export default {
    data () {
      return {
        showEmailInput: false,
        email: '',
        btnDisabled: false,
        errorMsg: '',
      }
    },
    created() {
      document.documentElement.classList.add('login');
    },
    beforeDestroy() {
      document.documentElement.classList.remove('login');
    },
    async mounted () {
      const query = this.$router.history.current.query;
      if (query.customToken && query.mode === 'signIn') {
        // If we have a custom token, sign in with that
        await this.magicLogin(null, query.customToken);
      } else if (query.apiKey && query.oobCode && query.mode === 'signIn') {
        // First, retrieve the email from local storage
        let email;
        if (query.email) {
          email = query.email;
        } else {
          email = localStorage.getItem('magicLoginEmail');
        }
        if (email) {
          await this.magicLogin(email, null);
        } else {
          // We don't have an email available - ask the user to enter one 
          this.showEmailInput = true;
        }
      } else {
        // We don't have the required information to process a magic sign in, redirect to login
        window.location = getBrandingOptions().constructMonoAuthSignInUrl(query.redirectPath);
      }
    },
    components: {
      EmiotHeaderImage
    },
    methods: {
      async magicLogin(email, customToken) {
        // If we're already logged in with the same email, don't bother processing this
        const currentUser = await this.$store.getters.firebaseUser;
        if (email != null && currentUser != null && currentUser.email === email) {
          await this.redirect();
          return;
        }

        const query = this.$router.history.current.query;
        this.errorMsg = '';
        try {
          this.btnDisabled = true;
          if (customToken != null) {
            await signInWithCustomToken(firebaseAuth, customToken);
          } else if (email != null) {
            await signInWithEmailLink(firebaseAuth, email);
          } else {
            // If we don't have an email, we can't sign in
            this.showEmailInput = true;
            return;
          }
        } catch (e) {
          if (e.code === 'auth/expired-action-code') {
            this.errorMsg = 'The sign-in link has expired. Please sign in again.';
          } else if (e.code === 'auth/invalid-action-code') {
            this.errorMsg = 'The sign-in link is invalid. Please sign in again.';
          } else if (e.code === 'auth/invalid-email') {
            if (this.showEmailInput === true) {
              this.errorMsg = 'The email is incorrect. Please check the email and try again.';
            } else {
              this.showEmailInput = true;
            }
          } else if (e.code === 'auth/user-disabled') {
            this.errorMsg = 'Your account has been disabled. Please contact WBS support to continue.';
          } else {
            this.errorMsg = translateFirebaseAuthError(e);
          }
          console.error('Failed to sign in with email link', e);
          window.location = getBrandingOptions().constructMonoAuthSignInUrl(query.redirectPath);
          return;
        } finally {
          localStorage.removeItem('magicLoginEmail');
          this.btnDisabled = false;
        }

        await this.redirect();
      },
      async submit () {
        await this.magicLogin(this.email);
      },
      async redirect () {
        if (!(await this.$store.dispatch('isLoggedIn'))) {
          return;
        }

        await this.$store.dispatch('fetchEmiotPermissions');
        await this.$store.dispatch('fetchGlobalPermissions')

        if (this.$router.history.current.query.redirectPath === undefined) {
          this.$router.history.current.query.redirectPath = '/';
        }

        let router = await this
            .$store
            .dispatch('reloadRouter', {router: this.$router});

        this.$router.matcher = router.matcher;

        if (this.$router.history.current.query.redirectPath !== undefined) {
          await this.$router.push(this.$router.history.current.query.redirectPath);
        } else {
          await this.$router.push({name: 'Home'});
        }
      },
    }
  }
</script>